import { Trans, useLingui } from '@lingui/react';
import { FC, useEffect, useState } from 'react';
import { Group, NumberInput, Select, Skeleton, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons';
import PanePaper from '../../Molecules/PanePaper';
import { DataS } from '../../Service/DataS';
import { SequentialNumberInput } from '../tiptap/TiptapEditorInput';
import { SeqNumberS, SequentialNumberDto, SequentialNumberType } from '../../Service/SeqNumberS';
import { ValidationS } from '../../Service/ValidationS';

const SequentialNumber: FC<{ sequentialNumberType: SequentialNumberType }> = ({ sequentialNumberType }) => {
  const { i18n } = useLingui();
  const [nextNumber, setNextNumber] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [loadingNextNumber, setLoadingNextNumber] = useState(false);
  const [sequentialNumberConfig, setSequentialNumberConfig] = useState<SequentialNumberDto | null>();
  const [showError, setShowError] = useState(false);
  const { pattern, minimumLength, sequentialNumber } = sequentialNumberConfig ?? {};

  useEffect(() => {
    setLoading(true);
    SeqNumberS.getSequentialNumber(sequentialNumberType)
      .then(setSequentialNumberConfig)
      .finally(() => setLoading(false));
  }, [sequentialNumberType]);

  useEffect(() => {
    if (sequentialNumberConfig) {
      setLoadingNextNumber(true);
      SeqNumberS.getNextSequentialNumber(sequentialNumberType)
        .then(setNextNumber)
        .finally(() => setLoadingNextNumber(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequentialNumberConfig]);

  const updateNumber = async <K extends keyof SequentialNumberDto>(key: K, val: any) => {
    const resJSON = await SeqNumberS.patchSequentialNumber(sequentialNumberType, { [key]: val });
    if (resJSON) {
      setSequentialNumberConfig(resJSON);
      return true;
    }
    return false;
  };

  const styles = {
    fieldContainer: {
      marginTop: 10,
      justifyContent: 'space-between',
    },
    field: {
      marginLeft: 5,
      marginTop: 20,
      display: 'flex',
      textAlign: 'right',
      flexDirection: 'column',
    },
    pattern: {
      marginRight: 5,
    },
    seqNo: {
      marginRight: 5,
      flex: 1,
    },
    nextInvoiceNumber: {
      fontWeight: 800,
      textAlign: 'right',
      fontSize: 17,
    },
  };

  const nextSequentialNumbertype = sequentialNumberType.split('_')[0].toLowerCase();

  return (
    <PanePaper title={`sequential-number.${sequentialNumberType.toLowerCase()}`}>
      <>
        {loading && <Skeleton width="100%" height={150} />}
        {!loading && sequentialNumberConfig && (
          <>
            <div style={styles.pattern}>
              <SequentialNumberInput
                value={pattern ?? ''}
                label="Muster mit Platzhaltern"
                patchField={(value) => {
                  if (ValidationS.validAwsObjectKeyName(value)) {
                    setShowError(false);
                    return updateNumber('pattern', value);
                  }
                  setShowError(true);
                  return Promise.resolve(true);
                }}
              />
              {showError && (
                <Text color="red" size="xs">
                  {i18n._('error.input-field.invalid-aws-char')}
                </Text>
              )}
            </div>
            <div style={styles.fieldContainer}>
              <div style={{ display: 'flex' }}>
                <div style={styles.seqNo}>
                  <NumberInput
                    hideControls
                    defaultValue={sequentialNumber}
                    label="Fortlaufende Nummer"
                    onBlur={(e) => {
                      updateNumber('sequentialNumber', e.currentTarget.value as any);
                    }}
                  />
                </div>
                <div style={styles.seqNo}>
                  <Select
                    data={DataS.getMinimumLengthOptions(i18n)}
                    label={i18n._('sequential-number.minimum-length')}
                    defaultValue={minimumLength?.toString()}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    rightSection={<IconChevronDown size={14} />}
                    rightSectionWidth={25}
                    onChange={(val) => updateNumber('minimumLength', Number(val))}
                  />
                </div>
              </div>
              <Text align="right" mt="md">
                {loadingNextNumber && (
                  <Group position="right">
                    <Skeleton height={25} width={125} />
                  </Group>
                )}
                {!loadingNextNumber && (
                  <>
                    <Trans id={`document.number.next.${nextSequentialNumbertype}`} />:
                    <Text weight="bolder" mt={5} size="md">
                      {nextNumber}
                    </Text>
                  </>
                )}
              </Text>
            </div>
          </>
        )}
      </>
    </PanePaper>
  );
};

export default SequentialNumber;
